'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LocaleLanguage } from '@/lib/locale-context';
import type { Consultation } from '../types';
import { fetchConsultationsFromStrapi } from '@/api/consultations';

type Topic = NonNullable<Consultation['topics']>[number];

const findConsultationByTitle = (
  consultations: Consultation[],
  title: string,
) => {
  if (!title) return null;
  return consultations.find((c) => c.title === title) ?? null;
};

const getTopicsFromConsultation = (
  consultation: Consultation | null,
): Topic[] => consultation?.topics ?? [];

export type UseConsultationFlowResult = {
  consultations: Consultation[];
  loadingConsultations: boolean;
  selectedConsultation: Consultation | null;
  topics: Topic[];
  selectedConsultationTitle: string;
  selectedTopics: string[];
  anliegen: string;
  setSelectedConsultationTitle: (v: string) => void;
  setSelectedTopics: React.Dispatch<React.SetStateAction<string[]>>;
  setAnliegen: (v: string) => void;
  resetDomain: () => void;
};

export const useConsultationFlow = (
  locale: LocaleLanguage = 'de',
  initialConsultations: Consultation[] = [],
  initialSelectedConsultationTitle = '',
): UseConsultationFlowResult => {
  const [consultations, setConsultations] =
    useState<Consultation[]>(initialConsultations);
  const [loadingConsultations, setLoadingConsultations] = useState(
    initialConsultations.length === 0,
  );

  const [selectedConsultationTitle, setSelectedConsultationTitle] = useState(
    initialSelectedConsultationTitle,
  );
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [anliegen, setAnliegen] = useState('');

  const resetDomain = useCallback(() => {
    setSelectedConsultationTitle('');
    setSelectedTopics([]);
    setAnliegen('');
  }, []);

  // Reset dependent state when consultation changes
  useEffect(() => {
    setSelectedTopics([]);
    setAnliegen('');
  }, [selectedConsultationTitle]);

  // Fetch consultations
  useEffect(() => {
    if (initialConsultations.length > 0) {
      setConsultations(initialConsultations);
      setLoadingConsultations(false);
      return;
    }

    let alive = true;

    (async () => {
      setLoadingConsultations(true);
      try {
        const data = await fetchConsultationsFromStrapi(locale);
        if (!alive) return;
        setConsultations(data);
      } catch (err) {
        console.error('Failed to load consultations', err);
        if (!alive) return;
        setConsultations([]);
      } finally {
        if (!alive) return;
        setLoadingConsultations(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [locale, initialConsultations]);

  const selectedConsultation = useMemo(
    () => findConsultationByTitle(consultations, selectedConsultationTitle),
    [consultations, selectedConsultationTitle],
  );

  const topics = useMemo(
    () => getTopicsFromConsultation(selectedConsultation),
    [selectedConsultation],
  );

  return {
    consultations,
    loadingConsultations,
    selectedConsultation,
    topics,
    selectedConsultationTitle,
    selectedTopics,
    anliegen,
    setSelectedConsultationTitle,
    setSelectedTopics,
    setAnliegen,
    resetDomain,
  };
};

export default useConsultationFlow;
